import { ChangeEvent } from "react";
import { Dayjs } from "dayjs";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { SaleDepositEditRequestStatusFilter } from "../../types/sale-deposit-edit-request.type";
import DateRangePicker from "../common/DateRangePicker";
import FilterField, {
  FILTER_ROW_BOTTOM_SPACING,
  FLOATING_HELPER_TEXT_SX,
} from "../SaleDepositReport/FilterField";
import {
  UNFILTERED_OPTION,
  useBranchRouteOptions,
} from "../SaleDepositReport/useBranchRouteOptions";
import {
  EDIT_REQUEST_STATUS_FILTER,
  isEditRequestStatusFilter,
} from "./editRequest.utils";

interface EditRequestFilterProps {
  status: SaleDepositEditRequestStatusFilter;
  onChangeStatus: (status: SaleDepositEditRequestStatusFilter) => void;
  dateFrom: Dayjs;
  dateTo: Dayjs;
  onChangeDateRange: (from: Dayjs, to: Dayjs) => void;
  /** Offered only to a cross-branch reader; a branch reader is scoped by their token. */
  canSelectBranch: boolean;
  selectedBranch: string;
  onChangeBranch: (branchCode: string) => void;
  selectedRoute: string;
  onChangeRoute: (routeCode: string) => void;
}

/**
 * The คำขอแก้ไขยอดโอน queue's filter row.
 *
 * Built from the same FilterField as the two รายงานการโอน screens, and offering the same
 * pickers, because this queue is where those screens' requests land — a filter that looked
 * or behaved differently here would read as a different report.
 *
 * The date range narrows the DEPOSIT's business date rather than the day the request was
 * raised: the approver is looking for the day whose money is in dispute.
 */
export default function EditRequestFilter({
  status,
  onChangeStatus,
  dateFrom,
  dateTo,
  onChangeDateRange,
  canSelectBranch,
  selectedBranch,
  onChangeBranch,
  selectedRoute,
  onChangeRoute,
}: EditRequestFilterProps) {
  const { branches, routeOptions, isRouteDisabled } = useBranchRouteOptions(
    canSelectBranch,
    selectedBranch,
  );

  const handleChangeStatus = (event: ChangeEvent<HTMLInputElement>) => {
    if (isEditRequestStatusFilter(event.target.value)) {
      onChangeStatus(event.target.value);
    }
  };

  // Routes belong to one branch, so a route picked under the old branch would filter the
  // list down to nothing rather than to that branch's requests.
  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeBranch(event.target.value);
    onChangeRoute(UNFILTERED_OPTION);
  };

  const handleChangeRoute = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeRoute(event.target.value);
  };

  return (
    <Grid container spacing={2} mb={FILTER_ROW_BOTTOM_SPACING}>
      <FilterField label="สถานะ">
        <TextField
          select
          size="small"
          fullWidth
          value={status}
          onChange={handleChangeStatus}
        >
          {EDIT_REQUEST_STATUS_FILTER.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="วันที่">
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={onChangeDateRange}
        />
      </FilterField>
      {canSelectBranch && (
        <FilterField label="สาขา">
          <TextField
            select
            size="small"
            fullWidth
            value={selectedBranch}
            onChange={handleChangeBranch}
          >
            <MenuItem value={UNFILTERED_OPTION}>ทั้งหมด</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.branch_code}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}
      <FilterField label="สายส่ง">
        <TextField
          select
          size="small"
          fullWidth
          disabled={isRouteDisabled}
          value={selectedRoute}
          onChange={handleChangeRoute}
          helperText={isRouteDisabled ? "เลือกสาขาก่อน" : undefined}
          sx={FLOATING_HELPER_TEXT_SX}
        >
          <MenuItem value={UNFILTERED_OPTION}>ทั้งหมด</MenuItem>
          {routeOptions.map((route) => (
            <MenuItem key={route.route_code} value={route.route_code}>
              {route.route_code} - {route.route_name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
    </Grid>
  );
}
